import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Calendar, Save, X, ArrowLeft, AlertCircle, User } from 'lucide-react';
import Navbar from '../../components/layout/Navbar';
import Footer from '../../components/layout/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import api from '../../utils/api';
import { getUser } from '../../utils/auth';
import { toast } from 'sonner';

const CreateLeaveRequestPage = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const user = getUser();
  
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [formData, setFormData] = useState({
    leave_type: 'annual',
    start_date: '',
    end_date: '',
    total_days: 1,
    reason: '',
    substitute_employee_id: '',
    attachment: ''
  });

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/hr-v2?status=active');
      setEmployees(response.data);
    } catch (error) {
      console.error('Failed to load employees:', error);
    }
  };

  const leaveTypes = [
    { value: 'annual', labelAr: 'إجازة سنوية', labelEn: 'Annual Leave' },
    { value: 'sick', labelAr: 'إجازة مرضية', labelEn: 'Sick Leave' },
    { value: 'emergency', labelAr: 'إجازة طارئة', labelEn: 'Emergency Leave' },
    { value: 'unpaid', labelAr: 'إجازة بدون راتب', labelEn: 'Unpaid Leave' },
    { value: 'maternity', labelAr: 'إجازة أمومة', labelEn: 'Maternity Leave' },
    { value: 'paternity', labelAr: 'إجازة أبوة', labelEn: 'Paternity Leave' },
    { value: 'hajj', labelAr: 'إجازة حج', labelEn: 'Hajj Leave' },
    { value: 'marriage', labelAr: 'إجازة زواج', labelEn: 'Marriage Leave' },
    { value: 'death', labelAr: 'إجازة وفاة', labelEn: 'Death Leave' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      
      // Calculate total days when dates change
      if ((field === 'start_date' || field === 'end_date') && updated.start_date && updated.end_date) {
        const start = new Date(updated.start_date);
        const end = new Date(updated.end_date);
        const diffTime = Math.abs(end - start);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
        updated.total_days = diffDays;
      }
      
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.start_date || !formData.end_date || !formData.reason) {
      toast.error(i18n.language === 'ar' ? 'يرجى ملء الحقول المطلوبة' : 'Please fill required fields');
      return;
    }
    
    setLoading(true);
    try {
      await api.post('/hr-v2/requests/leave', formData);
      toast.success(i18n.language === 'ar' ? 'تم إرسال الطلب بنجاح' : 'Request submitted successfully');
      navigate('/hr/requests');
    } catch (error) {
      toast.error(i18n.language === 'ar' ? 'فشل إرسال الطلب' : 'Failed to submit request');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Navbar />

      <main className="pt-24 pb-12 px-4">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate('/hr/requests')} className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              {i18n.language === 'ar' ? 'رجوع' : 'Back'}
            </Button>
            <div className="flex-1">
              <h1 className="text-3xl font-bold">
                {i18n.language === 'ar' ? 'طلب إجازة جديد' : 'New Leave Request'}
              </h1>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  {i18n.language === 'ar' ? 'تفاصيل الإجازة' : 'Leave Details'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Leave Type */}
                <div>
                  <Label htmlFor="leave_type">
                    {i18n.language === 'ar' ? 'نوع الإجازة' : 'Leave Type'} *
                  </Label>
                  <Select value={formData.leave_type} onValueChange={(val) => handleInputChange('leave_type', val)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {leaveTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {i18n.language === 'ar' ? type.labelAr : type.labelEn}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Dates */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="start_date">
                      {i18n.language === 'ar' ? 'تاريخ البداية' : 'Start Date'} *
                    </Label>
                    <Input
                      id="start_date"
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => handleInputChange('start_date', e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="end_date">
                      {i18n.language === 'ar' ? 'تاريخ النهاية' : 'End Date'} *
                    </Label>
                    <Input
                      id="end_date"
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => handleInputChange('end_date', e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="total_days">
                      {i18n.language === 'ar' ? 'عدد الأيام' : 'Total Days'}
                    </Label>
                    <Input
                      id="total_days"
                      type="number"
                      value={formData.total_days}
                      readOnly
                      className="bg-gray-100"
                    />
                  </div>
                </div>

                {/* Reason */}
                <div>
                  <Label htmlFor="reason">
                    {i18n.language === 'ar' ? 'سبب الإجازة' : 'Reason'} *
                  </Label>
                  <Textarea
                    id="reason"
                    rows={4}
                    value={formData.reason}
                    onChange={(e) => handleInputChange('reason', e.target.value)}
                    placeholder={i18n.language === 'ar' ? 'اذكر سبب الإجازة...' : 'Describe the reason...'}
                    required
                  />
                </div>

                {/* Substitute Employee */}
                <div>
                  <Label htmlFor="substitute_employee_id">
                    {i18n.language === 'ar' ? 'الموظف البديل' : 'Substitute Employee'}
                  </Label>
                  <Select 
                    value={formData.substitute_employee_id} 
                    onValueChange={(val) => handleInputChange('substitute_employee_id', val)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={i18n.language === 'ar' ? 'اختر الموظف البديل...' : 'Select substitute...'} />
                    </SelectTrigger>
                    <SelectContent>
                      {employees.map((emp) => (
                        <SelectItem key={emp.id} value={emp.id}>
                          {emp.full_name_ar} - {emp.position}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">
                    {i18n.language === 'ar' ? 'اختياري - في حالة الحاجة لموظف بديل' : 'Optional - if substitute is needed'}
                  </p>
                </div>

                {/* Info Box */}
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div>
                      <h5 className="font-bold text-blue-900 mb-1">
                        {i18n.language === 'ar' ? 'مسار الموافقة' : 'Approval Process'}
                      </h5>
                      <p className="text-sm text-blue-800">
                        {i18n.language === 'ar' 
                          ? 'سيتم إرسال طلبك للمدير المباشر ثم إلى قسم الموارد البشرية للموافقة.'
                          : 'Your request will be sent to your direct manager then to HR for approval.'
                        }
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex gap-4 justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/hr/requests')}
                    disabled={loading}
                  >
                    <X className="w-4 h-4 mr-2" />
                    {i18n.language === 'ar' ? 'إلغاء' : 'Cancel'}
                  </Button>
                  <Button
                    type="submit"
                    className="btn-primary gap-2"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        {i18n.language === 'ar' ? 'جاري الإرسال...' : 'Submitting...'}
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        {i18n.language === 'ar' ? 'إرسال الطلب' : 'Submit Request'}
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </form>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CreateLeaveRequestPage;
